import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { Monitor, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { setTheme } from '@/store/slices/uiSlice';
import { Logo } from './Logo';

/**
 * Chrome for the candidate-facing pages: apply, interview, book. Deliberately lighter
 * than the recruiter console — no navigation, nothing to sign into.
 */
/** The three things the CV pages do, named the way people look for them. */
const CV_TOOLS = [
  { to: '/resume-check', label: 'Check my CV', short: 'Check', footer: 'ATS score checker' },
  { to: '/resume-builder', label: 'Build a CV', short: 'Build', footer: 'AI resume maker' },
  { to: '/resume-check/restyle', label: 'Change design', short: 'Design', footer: 'Change resume template' },
  { to: '/my-cvs', label: 'My CVs', short: 'Mine', footer: 'My saved CVs' },
];

/**
 * The guides and the pages about the site, in every CV page's footer: the one place
 * that links each of them from everywhere, so none depends on being found by search.
 */
const CV_GUIDES = [
  { to: '/resume-templates', label: 'Resume templates' },
  { to: '/resume-format-for-freshers', label: 'Resume format for freshers' },
  { to: '/resume-kaise-banaye', label: 'Resume kaise banaye' },
  { to: '/ats-friendly-resume', label: 'ATS-friendly resume' },
  { to: '/resume-for-bcom-freshers', label: 'B.Com fresher resume' },
  { to: '/about', label: 'About' },
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/refund-policy', label: 'Refunds' },
  { to: '/shipping-policy', label: 'Delivery' },
  { to: '/contact', label: 'Contact' },
];

export function PublicShell({
  children,
  wide,
  /**
   * The line under the footer rule. Overridable because the default speaks about "this
   * application", which is true of the pages a candidate reaches from an invitation and
   * false on the CV checker, where nothing is submitted and nothing is kept.
   */
  note = 'Your details are used only for this application.',
  cvTools = false,
}: {
  children: ReactNode;
  /**
   * `true` for the ordinary two-column pages, `'document'` for the one that has to
   * hold a sheet of A4 at its real size beside something else.
   */
  wide?: boolean | 'document';
  note?: string;
  /**
   * The CV tools' own links, in the header and the footer. Only on those pages: a
   * candidate halfway through an application or an interview is never offered a way
   * off it.
   */
  cvTools?: boolean;
}) {
  const theme = useAppSelector((state) => state.ui.theme);
  const dispatch = useAppDispatch();

  const next = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
  const Icon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;

  /**
   * How far the content spreads, used by the header, the body and the footer alike so
   * the logo stays over the first column.
   *
   * 1024px left the two-column layout squeezed into the middle of a desktop screen
   * with a third of the window empty either side; 1480px went the other way and ran
   * the content to within a thumb's width of the edges, which reads as stretched
   * rather than generous. 1240 is the middle: the columns get their room and the page
   * still sits on the canvas instead of filling it.
   *
   * The mark-up screen is the exception and gets 1560. A4 is 794px wide, and at 1240
   * the column holding it could never be more than 480 — so the sheet was always
   * drawn at sixty per cent, which is below the size at which you can read your own
   * CV. The extra 320px is what buys the page at its real size, and nothing else on
   * the site needs it.
   */
  const width = wide === 'document' ? 'max-w-[1560px]' : wide ? 'max-w-[1240px]' : 'max-w-3xl';

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <header className="sticky top-0 z-30 border-b border-line bg-canvas/85 backdrop-blur-md">
        <div className={`mx-auto flex h-14 w-full ${width} items-center justify-between gap-2 px-4 min-[400px]:px-5 sm:px-8`}>
          <Logo compact={cvTools} />
          <div className="flex items-center gap-1">
            {cvTools && (
              <nav aria-label="CV tools" className="flex items-center gap-0.5">
                {CV_TOOLS.map(({ to, label, short }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === '/resume-check'}
                    className={({ isActive }) =>
                      `rounded-md px-1 py-1.5 text-[13px] font-medium transition-colors min-[380px]:px-1.5 min-[400px]:px-2 sm:px-3 ${
                        isActive ? 'bg-brand-soft text-brand' : 'text-ink-muted hover:bg-surface-raised hover:text-ink'
                      }`
                    }
                  >
                    <span className="sm:hidden">{short}</span>
                    <span className="hidden sm:inline">{label}</span>
                  </NavLink>
                ))}
              </nav>
            )}
            <Button variant="ghost" size="icon-sm" onClick={() => dispatch(setTheme(next))} aria-label="Change theme">
              <Icon />
            </Button>
          </div>
        </div>
      </header>

      {/*
        Clipped here rather than inside the page, because this is the first element
        that is actually the width of the window. A clip set on the content column
        cuts every full-bleed band and backdrop back to the column — which is why the
        hero's wash stopped short of the edges while the bands behind it did not.
      */}
      <main className="flex-1 overflow-x-clip px-5 py-8 sm:px-8 sm:py-12">
        <div className={`mx-auto w-full ${width}`}>{children}</div>
      </main>

      <footer className="border-t border-line px-5 py-5 sm:px-8">
        {cvTools && (
          <nav
            aria-label="Guides"
            className={`mx-auto mb-3 flex w-full ${width} flex-wrap gap-x-4 gap-y-1 border-b border-line pb-3 text-[12px] text-ink-subtle`}
          >
            {CV_GUIDES.map(({ to, label }) => (
              <NavLink key={to} to={to} className="inline-flex min-h-6 items-center hover:text-brand hover:underline">
                {label}
              </NavLink>
            ))}
          </nav>
        )}
        <div className={`mx-auto flex w-full ${width} flex-col gap-2 text-[12px] text-ink-subtle sm:flex-row sm:items-center sm:justify-between`}>
          <p>Powered by HireOrbit · {note}</p>
          {cvTools && (
            <nav aria-label="CV tools" className="flex flex-wrap gap-x-4 gap-y-1">
              {CV_TOOLS.map(({ to, footer }) => (
                <NavLink key={to} to={to} className="inline-flex min-h-6 items-center hover:text-brand hover:underline">
                  {footer}
                </NavLink>
              ))}
              {/* The bare domain opens the CV checker now, so the console needs a way in. */}
              <NavLink to="/login" className="inline-flex min-h-6 items-center hover:text-brand hover:underline">
                Recruiters: sign in
              </NavLink>
            </nav>
          )}
        </div>
      </footer>
    </div>
  );
}
